import React, { Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NetworkStatusBanner from "components/NetworkStatusBanner";
import Spinner from "components/spinner";
import Loader from "components/loader";
import NotFound from "pages/NotFound";
import LandingPage from './pages/landing-page';
import AboutPage from './pages/about-page';
import Admissions from './pages/Admissions';
import ExamCreationManagement from './pages/exam-creation-management';
import ComputerBasedTestingPlatform from './pages/computer-based-testing-cbt-platform';
import LoginAuthentication from './pages/login-authentication';
import StudentDashboard from './pages/student-dashboard';
import ResultsAnalyticsPortal from './pages/results-analytics-portal';
import ProfilePage from './pages/profile';
import AccountSettingsPage from './pages/account-settings';
import TeacherPortal from './pages/teacher-portal';
import ContactPage from './pages/contact';
import PrivacyPolicy from './pages/privacy-policy';
import TermsOfService from './pages/terms-of-service';
import ResultPortal from './pages/exam-creation-management/components/ResultPortal';
import PostAnnouncement from './pages/teacher-portal/components/post-announcement';
import AdminDashboard from './pages/admin';
// Add these imports if needed
// import Announcements from './pages/announcements';
// import AdminDashboard from './pages/admin-dashboard';

// PublicRoute should just render the element
const PublicRoute = ({ element: Element }) => <Element />;

// Added ProtectedRouteGuard component
const ProtectedRouteGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get user and authentication status
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAuthenticated = user && user.isAuthenticated;
  const protectedPaths = [
    "/admin", "/student-dashboard", "/teacher-portal", "/parent-dashboard"
  ];
  const isProtected = protectedPaths.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    const handlePopState = (event) => {
      // If authenticated and on protected route, logout immediately when navigating to public page
      if (isAuthenticated && isProtected) {
        const nextPath = window.location.pathname;
        const isGoingToPublic = !protectedPaths.some((p) => nextPath.startsWith(p));
        if (isGoingToPublic) {
          localStorage.removeItem("user");
          window.location.replace("/login-authentication");
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [location.pathname, isAuthenticated, isProtected]);

  return <>{children}</>;
};

const AppWrapper = ({ children }) => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const isAuthenticated = user && user.isAuthenticated;
    const isDashboard =
      window.location.pathname.startsWith("/teacher-portal") ||
      window.location.pathname.startsWith("/student-dashboard") ||
      window.location.pathname.startsWith("/admin-dashboard") ||
      window.location.pathname.startsWith("/parent-dashboard");

    // If not authenticated or not on dashboard, do not set timer or listeners
    if (!isAuthenticated || !isDashboard) return;

    let timer = null;

    // Use a stable reference for the resetTimer function
    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        localStorage.removeItem("user");
        alert("Session expired due to inactivity.");
        window.location.replace("/login-authentication");
      }, 60 * 60 * 1000); // 1 hour (increased from 30 minutes)
    };

    // Attach listeners only once per mount
    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);

    // Start timer on mount
    resetTimer();

    // Clean up listeners and timer on unmount
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
    };
  }, []); // <-- Remove navigate from deps to avoid remounts

  return (
    <>
      <NetworkStatusBanner />
      {children}
    </>
  );
};

// Loader logic for entrance/exit transitions
const PageTransitionWrapper = ({ children }) => {
  const location = useLocation();
  const [showLoader, setShowLoader] = useState(false);
  const [showSpinner, setShowSpinner] = useState(true); // Spinner always shows first
  const prevPath = React.useRef(location.pathname);

  useEffect(() => {
    // Show loader only on landing page
    if (
      location.pathname === "/" ||
      location.pathname === "/landing-page"
    ) {
      setShowLoader(true);
      setShowSpinner(false);
      const timer = setTimeout(() => setShowLoader(false), 1200); // Loader duration
      prevPath.current = location.pathname;
      return () => clearTimeout(timer);
    } else {
      // Show spinner for all other routes
      setShowSpinner(true);
      setShowLoader(false);
      const timer = setTimeout(() => setShowSpinner(false), 600); // Spinner duration
      prevPath.current = location.pathname;
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  if (showLoader) return <Loader />;
  if (showSpinner)
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.8)",
          minHeight: "100vh",
          width: "100vw",
        }}
      >
        <Spinner message="Loading page..." />
      </div>
    );
  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <AppWrapper>
          {/* Wrap dashboard/protected routes with ProtectedRouteGuard */}
          <Suspense fallback={<Spinner message="Loading page..." />}>
            <RouterRoutes>
              {/* Public pages wrapped with PublicRoute */}
              <Route path="/" element={<PublicRoute element={LandingPage} />} />
              <Route path="/landing-page" element={<PublicRoute element={LandingPage} />} />
              <Route path="/about-page" element={<PublicRoute element={AboutPage} />} />
              {/* Uncomment and import Announcements if needed */}
              {/* <Route path="/announcements" element={<PublicRoute element={Announcements} />} /> */}
              <Route path="/contact" element={<PublicRoute element={ContactPage} />} />
              <Route path="/login-authentication" element={<PublicRoute element={LoginAuthentication} />} />
              <Route path="/privacy-policy" element={<PublicRoute element={PrivacyPolicy} />} />
              <Route path="/terms-of-service" element={<PublicRoute element={TermsOfService} />} />
              {/* Protected/dashboard pages */}
              <Route
                path="/admissions"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <Admissions />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/exam-creation-management"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <ExamCreationManagement />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/exam-creation-management/result-portal"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <ResultPortal />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/computer-based-testing-cbt-platform"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <ComputerBasedTestingPlatform />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/student-dashboard"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <StudentDashboard />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/results-analytics-portal"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <ResultsAnalyticsPortal />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <ProfilePage />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/account-settings"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <AccountSettingsPage />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/teacher-portal"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <TeacherPortal />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="/post-announcement"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <PostAnnouncement />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              {/* Admin dashboard route */}
              <Route
                path="/admin"
                element={
                  <ProtectedRouteGuard>
                    <PageTransitionWrapper>
                      <AdminDashboard />
                    </PageTransitionWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
          </Suspense>
        </AppWrapper>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
